import React, { useReducer, useState, useMemo } from "react";
import PropTypes from "prop-types";
import StockOpnameTableHistory from "../../elements/Tables/StockOpnameTable/StockOpnameTableHistory";
import { useFetch } from "../../../Utils/useFetch";
import { FETCH_ALL_STOCKOPNAME_API, FETCH_ALL_COUNTER_API } from "../../../Constants/Apis";
import { Input, Checkbox, Button, Select, Card, Statistic } from "antd";
import * as XLSX from "xlsx";
import { reducer } from "../../../Utils/CommonUtils";
import { updateStockQuantity } from "../../../Services/StockService";

const { Option } = Select;

const StockOpnamePageHistory = ({ history, userData }) => {
  const initialState = {
    filteredInfo: {},
    sorter: {},
    current: 1,
    pageSize: 10,
  };

  const [selectedCounterCode, setSelectedCounterCode] = useState(
    userData?.counterCode === "ECOMMRI" ? "ECOMMRI" : null
  );

  const [state, dispatch] = useReducer(reducer, initialState);
  const [showNotMatchedOnly, setShowNotMatchedOnly] = useState(false);

  // Fetch counters to populate selection
  const { fetchedData: countersRes } = useFetch(FETCH_ALL_COUNTER_API);

  const stockOpnameApiUrl = useMemo(() => {
    if (!selectedCounterCode) return null;
    if (selectedCounterCode === "All") return `${FETCH_ALL_STOCKOPNAME_API}/all`;
    return `${FETCH_ALL_STOCKOPNAME_API}/${selectedCounterCode}`;
  }, [selectedCounterCode]);

  const { fetchedData, callReFetch, loading } = useFetch(
    stockOpnameApiUrl,
    state,
    null,
    { enabled: false }
  );

  const handleSearch = () => {
    if (selectedCounterCode) {
      callReFetch();
    }
  };

  const rawData = fetchedData?.data || [];

  const visibleData = useMemo(() => {
    let data = rawData;
    if (userData?.counterCode === "ECOMMRI") {
      data = data.filter((item) => item.counterCode === "ECOMMRI");
    } else if (userData?.counterCode) {
      data = data.filter((item) => item.counterCode !== "ECOMMRI");
    }

    // Double check filter for selectedCounterCode if it's not "All"
    // although backend should already have filtered it.
    if (selectedCounterCode && selectedCounterCode !== "All") {
      data = data.filter((item) => item.counterCode === selectedCounterCode);
    }
    return data;
  }, [rawData, userData?.counterCode, selectedCounterCode]);

  const uniqueCounterCodes = useMemo(() => {
    const allCounters = countersRes?.data || [];
    let codes = allCounters.map((c) => c.code).filter(Boolean);

    if (userData?.counterCode === "ECOMMRI") {
      return ["ECOMMRI"];
    } else {
      codes = codes.filter((c) => c !== "ECOMMRI");
      return ["All", ...Array.from(new Set(codes))];
    }
  }, [countersRes?.data, userData?.counterCode]);
  

  // Pagination info (used by card)
  const paginationInfo = {
    current: state.current,
    pageSize: state.pageSize,
    total: visibleData.length,
  };

  const handleCounterCodeChange = (value) => {
    setSelectedCounterCode(value);
    dispatch({ type: "current", payload: 1 });
  };

  const handlePaginationChange = (page, pageSize) => {
    dispatch({ type: "current", payload: page });
    dispatch({ type: "pageSize", payload: pageSize });
    callReFetch();
  };

  const handleCheckboxChange = (e) => {
    setShowNotMatchedOnly(e.target.checked);
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(visibleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "StockOpnameHistory");
    XLSX.writeFile(wb, "StockOpnameHistory.xlsx");
  };

  return (
    <div
      style={{
        width: "100vw",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Top toolbar */}
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "80vw",
          maxWidth: "100vw",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        {/* Total items card */}
        <Card
          size="small"
          style={{ minWidth: 220, flex: "0 0 auto" }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Statistic
            title="Total Items"
            value={paginationInfo.total}
            valueStyle={{ fontWeight: 700 }}
            // show thousands separator
            formatter={(val) => Number(val).toLocaleString()}
          />
          {selectedCounterCode ? (
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              Filtered by: <strong>{selectedCounterCode}</strong> (Click Search to fetch)
            </div>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              <em>Please select a counter code</em>
            </div>
          )}
        </Card>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* Counter code selector */}
          <Select
            showSearch
            style={{ width: 220, flex: "0 0 auto" }}
            placeholder="Select a counter code"
            optionFilterProp="children"
            onChange={handleCounterCodeChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            value={selectedCounterCode}
          >
            {uniqueCounterCodes.map((code) => (
              <Option key={code} value={code}>
                {code}
              </Option>
            ))}
          </Select>
          <Button
            type="primary"
            onClick={handleSearch}
            disabled={!selectedCounterCode}
            loading={loading}
          >
            Search
          </Button>
        </div>

        <Button
          type="primary"
          onClick={handleExport}
          style={{ flex: "0 0 auto" }}
        >
          Export to Excel
        </Button>
      </div>

      <StockOpnameTableHistory
        loading={loading}
        data={visibleData}
        pagination={paginationInfo}
        dispatch={dispatch}
        userData={userData}
        callRefetch={callReFetch}
        updateStockQuantity={updateStockQuantity}
        onPaginationChange={handlePaginationChange}
      />
    </div>
  );
};

StockOpnamePageHistory.propTypes = {
  history: PropTypes.object,
  userData: PropTypes.object,
};

export default StockOpnamePageHistory;
